import _ from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { Button, Modal, Tooltip } from "@mantine/core";
import { IState } from "../../data/reducers/state";
import {
  IAppointment,
  IAppointmentDetail,
  IGameAppointmentDetail,
  ISupervisorAppointmentDetail,
} from "../../types/appointments/Appointment";
import { AppointmentStatus } from "../../types/appointments/AppointmentStatus";
import { AppointmentType } from "../../types/appointments/AppointmentType";
import { IGame } from "../../types/game/Game";
import { IPerson } from "../../types/persons/person";
import { IVenue } from "../../types/venue/Venue";
import { toDateTime } from "../../util/date+helper";
import { PersonLink } from "../common/Links";
import { CenteredLoader } from "../common/loader";
import { SSTable } from "../common/TableData";
import AppointmentView from "./AppointmentView";

export interface IAppointmentViewProps {
  person: string;
  appointmentIds: string[];
  appointments: IAppointment<IAppointmentDetail>[];

  personObj: IPerson;
  persons: { [key: string]: IPerson };
  games: { [key: string]: IGame };
  venues: { [key: string]: IVenue };
}

class AppointmentPersonSummaryView extends React.PureComponent<IAppointmentViewProps, { appointmentToShow?: string | null }> {
  constructor(props: IAppointmentViewProps) {
    super(props);
    this.state = {
      appointmentToShow: null,
    };
  }

  private getRowForAppointment(appointment: IAppointment<IAppointmentDetail>, setAppointmentToShow) {
    if (appointment.type == AppointmentType.game) {
      // const details = appointment.details as IGameAppointmentDetail;
      return (
        <GameAppointmentDetails
          appointment={appointment as IAppointment<IGameAppointmentDetail>}
          // game={null}
          venues={this.props.venues}
          persons={this.props.persons}
          viewAppointment={setAppointmentToShow}
        />
      );
    }
    if (appointment.type == AppointmentType.supervisor) {
      return (
        <SupervisorAppointmentDetails
          appointment={
            appointment as IAppointment<ISupervisorAppointmentDetail>
          }
          persons={this.props.persons}
        />
      );
    }

    return (
      <p>
        Unrecognised appointment type {appointment.type} ({appointment._id})
      </p>
    );
  }


  public render() {
    const { appointments } = this.props;

    if (!appointments) return <CenteredLoader />;

    if (appointments.length === 0) return <p>No appointments</p>;
    const relevantApts: IAppointment<IGameAppointmentDetail>[] = _.sortBy(
      appointments.filter(
        (a) => a.stillAppointed,
      ) as IAppointment<IGameAppointmentDetail>[],
      (ap) => ap.start || ap.dateMarkedCompleted,
    );

    const setAppointmentToShow = (appointmentId: string | null) =>
      this.setState({ appointmentToShow: appointmentId });
    const appointmentToShow = this.state.appointmentToShow;


    return (
      <>
        <hr />
        <SSTable >
          {relevantApts.map((ap) => (
            <tr
              key={ap._id}
              style={{
                padding: "10px",
                backgroundColor: AppointmentStatus.requiresAction(ap.status)
                  ? "#ffb9b9"
                  : "",
              }}
            >
              {this.getRowForAppointment(ap, setAppointmentToShow)}
            </tr>
          ))}
        </SSTable>
        <Modal opened={!!appointmentToShow} onClose={() => setAppointmentToShow(null)} title="Appointment">
          <AppointmentView appointmentId={appointmentToShow} disableEdits={true} />
          <Button
            color="gray"
            style={{ margin: "10px 0 0 auto", display: "block" }}
            onClick={() => setAppointmentToShow(null)}
          >
            Close
          </Button>
        </Modal>

      </>
    );
  }
}

interface IGameAppointmentDetailProps {
  appointment: IAppointment<IGameAppointmentDetail>;
  // game: IGame;
  venues: { [key: string]: IVenue };
  persons: { [key: string]: IPerson };
  viewAppointment: (appointmentId: string) => void;
}

const GameAppointmentDetails: React.FunctionComponent<
  IGameAppointmentDetailProps
> = ({
  appointment,
  // game,
  venues,
  persons,
  viewAppointment,
}) => {
    // if (!game) return <p>{appointment.type}</p>;
    let allocatorText: JSX.Element | null = null;
    const allocatedStatus = (appointment.statuses || []).filter(
      (st) => st.status == AppointmentStatus.rostered,
    );
    if (allocatedStatus && allocatedStatus.length > 0) {
      const { person } = allocatedStatus[0];
      if (person && person in persons) {
        allocatorText = (
          <p>
            Allocated by{" "}
            <span
              id={`a${appointment._id}-allocated-time`}
              style={{ textDecoration: "underline" }}
            >
              {persons[person].fullName}
            </span>
            <Tooltip
              label={`Allocated on ${toDateTime(allocatedStatus[0].date).toLocaleString(
                DateTime.DATETIME_FULL,
              )}`}
            >
              <span />
            </Tooltip>
          </p>
        );
      }
    }

    return (
      <tr>
        <td>
          {toDateTime(appointment.start).toLocaleString(DateTime.DATETIME_MED)}
        </td>
        <td>
          {appointment.details?.court} (
          {AppointmentStatus.toString(appointment.status)})
        </td>
        <td style={{ color: "var(--color-text-medium)", marginBottom: "5px" }}>
          <em>{venues[appointment.details?.venue].name}</em>
        </td>
        <td>
          {appointment.status == AppointmentStatus.rejected &&
            `Reason: ${appointment.statuses.filter(
              (s) => s.status == AppointmentStatus.rejected,
            )[0].comment
            }`}
        </td>
        <td>
          {allocatorText && <span>{allocatorText}</span>}
        </td>
        <td><button onClick={() => viewAppointment(appointment._id)}>View</button></td>
      </tr>
    );
  };

interface ISupervisorAppointmentDetailProps {
  appointment: IAppointment<ISupervisorAppointmentDetail>;
  persons: { [key: string]: IPerson };
}

const SupervisorAppointmentDetails: React.FunctionComponent<
  ISupervisorAppointmentDetailProps
> = ({ appointment, persons }) => {
  let allocatorText: JSX.Element | null = null;
  const allocatedStatus = appointment.statuses.filter(
    (st) => st.status == AppointmentStatus.rostered,
  );
  if (allocatedStatus && allocatedStatus.length > 0) {
    const { person } = allocatedStatus[0];
    if (person && person in persons) {
      allocatorText = (
        <p>
          Allocated by{" "}
          <PersonLink identifier={person}>
            {persons[person].fullName}
          </PersonLink>
        </p>
      );
    }
  }
  return (
    <>
      <p style={{ marginBottom: "5px" }}>
        {toDateTime(appointment.start).toLocaleString(DateTime.DATETIME_MED)} -{" "}
        {toDateTime(appointment.finish).toFormat("hh:mm A")} (
        {AppointmentStatus.toString(appointment.status)})
      </p>
      {allocatorText && <span>{allocatorText}</span>}
    </>
  );
};

const mapState2Props = (
  state: IState,
  props: IAppointmentViewProps,
): Partial<IAppointmentViewProps> => {
  const q: Partial<IAppointmentViewProps> = {
    games: state.games,
    personObj: state.persons.map[props.person],
    persons: state.persons.map,
    venues: state.venues.map,
  };

  if (!props.appointments) {
    const appointmentIds =
      props.appointmentIds || state.appointments.byPerson[props.person];
    if (appointmentIds) {
      q.appointments = appointmentIds.map(
        (apid) => state.appointments.map[apid],
      );
    }
  }

  return q;
};

export default connect(mapState2Props)(AppointmentPersonSummaryView);
